import React, { useState } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { Box, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';

const minuteSeconds = 60;
const hourSeconds = 3600;
const daySeconds = 86400;

const timerProps = {
    isPlaying: true,
    size: 120,
    strokeWidth: 6,
};

const renderTime = (dimension: string, time: number, fontSize: number = 16) => {
    return (
        <Box textAlign="center">
            <Typography sx={{ fontSize: fontSize, fontFamily: FONT_ENUM.MAIN_BOLD }}>{time}</Typography>
            <Typography sx={{ fontSize: fontSize / 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>{dimension}</Typography>
        </Box>
    );
};

const getTimeSeconds = (time: number) => (minuteSeconds - time) | 0;
const getTimeMinutes = (time: number) => ((time % hourSeconds) / minuteSeconds) | 0;
const getTimeHours = (time: number) => ((time % daySeconds) / hourSeconds) | 0;
const getTimeDays = (time: number) => (time / daySeconds) | 0;

interface CountdownTimerProps {
    targetDate: string;
    size?: number;
    fontSize?: number;
    onEnd?: () => void;
    accentColor?: string;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate, size = 80, fontSize, onEnd, accentColor }) => {
    const stratTime = Date.now() / 1000;
    const endTime = new Date(targetDate).getTime() / 1000;

    const [timersCompleted, setTimersCompleted] = useState({
        days: false,
        hours: false,
        minutes: false,
        seconds: false,
    });

    const remainingTime = Math.max(0, endTime - stratTime);
    const days = Math.ceil(remainingTime / daySeconds);
    const daysDuration = days * daySeconds;

    const handleTimerComplete = (unit: 'days' | 'hours' | 'minutes' | 'seconds') => {
        setTimersCompleted(prev => {
            const updated = { ...prev, [unit]: true };
            const allComplete = Object.values(updated).every(val => val);

            if (allComplete && onEnd) {
                onEnd();
            }

            return updated;
        });
    };

    const ac = accentColor as `#${string}` | undefined;

    return (
        <Box display="flex" justifyContent="center" gap={4}>
            {/* Days Timer */}
            <CountdownCircleTimer
                {...timerProps}
                colors={ac ?? ('#7E2E84' as `#${string}`)}
                duration={daysDuration}
                isSmoothColorTransition
                size={size}
                onComplete={() => {
                    handleTimerComplete('days');
                    return { shouldRepeat: false };
                }}
                initialRemainingTime={remainingTime}>
                {({ elapsedTime, color }) => (
                    <Box color={color}>{renderTime('Days', getTimeDays(daysDuration - elapsedTime), fontSize)}</Box>
                )}
            </CountdownCircleTimer>
            {/* Hours Timer */}
            <CountdownCircleTimer
                {...timerProps}
                {...(ac
                    ? { colors: ac }
                    : {
                          colors: ['#D14081', '#FF1B72', '#a60c46', '#A30000'] as [
                              `#${string}`,
                              `#${string}`,
                              `#${string}`,
                              `#${string}`,
                          ],
                          colorsTime: [7, 5, 2, 0] as [number, number, number, number],
                      })}
                duration={daySeconds}
                isSmoothColorTransition
                size={size}
                initialRemainingTime={remainingTime % daySeconds}
                onComplete={() => {
                    handleTimerComplete('hours');
                    return { shouldRepeat: !timersCompleted.days };
                }}>
                {({ elapsedTime, color }) => (
                    <Box color={color}>{renderTime('Hours', getTimeHours(daySeconds - elapsedTime), fontSize)}</Box>
                )}
            </CountdownCircleTimer>
            {/* Minutes Timer */}
            <CountdownCircleTimer
                {...timerProps}
                colors={ac ?? ('#EF798A' as `#${string}`)}
                duration={hourSeconds}
                isSmoothColorTransition
                size={size}
                initialRemainingTime={remainingTime % hourSeconds}
                onComplete={() => {
                    handleTimerComplete('minutes');
                    return { shouldRepeat: !timersCompleted.hours && !timersCompleted.days };
                }}>
                {({ elapsedTime, color }) => (
                    <Box color={color}>
                        {renderTime('Minutes', getTimeMinutes(hourSeconds - elapsedTime), fontSize)}
                    </Box>
                )}
            </CountdownCircleTimer>
            {/* Seconds Timer */}
            <CountdownCircleTimer
                {...timerProps}
                {...(ac
                    ? { colors: ac }
                    : {
                          colors: ['#218380', '#FF1B72', '#a60c46', '#A30000'] as [
                              `#${string}`,
                              `#${string}`,
                              `#${string}`,
                              `#${string}`,
                          ],
                          colorsTime: [minuteSeconds, 50, 30, 0] as [number, number, number, number],
                      })}
                isSmoothColorTransition
                size={size}
                duration={minuteSeconds}
                initialRemainingTime={remainingTime % minuteSeconds}
                onComplete={() => {
                    handleTimerComplete('seconds');
                    return {
                        shouldRepeat: !timersCompleted.minutes && !timersCompleted.hours && !timersCompleted.days,
                    };
                }}>
                {({ elapsedTime, color }) => (
                    <Box color={color}>{renderTime('Seconds', getTimeSeconds(elapsedTime), fontSize)}</Box>
                )}
            </CountdownCircleTimer>
        </Box>
    );
};

export default CountdownTimer;
